import styled from 'styled-components';

import { AiLight16 } from 'ui/icons';
import { ClickTip } from 'ui/popover';
import { Body2NoMargin } from 'modules/frequencyManagement/components/Content.styles';
import { HistogramDataPoint } from 'modules/frequencyManagement/lib/types';
import { Caption, Overline, colors, spacing, borderRadius } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { transformToVisualPercents } from 'modules/ai/utils/histogramCalculations';

const OptimizeResultsDataRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 260px;
  align-items: center;
  gap: ${spacing.half};
  background: ${colors.backgroundSecondary};
  border: ${spacing.single} solid ${colors.borderPrimarySubtle};
  border-radius: ${borderRadius.md};
  padding: ${spacing.base};
`;

const HistogramContainer = styled.div`
  display: flex;
  gap: ${spacing.quarter};
  grid-column: 1 / span 2;
  div:first-child {
    span[data-bgcolor] {
      border-top-left-radius: ${pxToRem(4)};
      border-bottom-left-radius: ${pxToRem(4)};
    }
  }

  div:last-child {
    span[data-bgcolor] {
      border-top-right-radius: ${pxToRem(4)};
      border-bottom-right-radius: ${pxToRem(4)};
    }
  }
`;

const HistogramItem = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
`;

const HistogramSends = styled(Caption)`
  align-self: center;
  margin-bottom: ${spacing.eighth};
  font-size: 12px;
`;

export const HistogramTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

const histogramBackgroundColors = [colors.purple100, '#77327C', '#833E8B', '#904A9A', '#9C55A8'];
const createHistogramBackgroundColors = () => {
  let colorsStyling = ``;
  for (let i = 0; i < 5; i++) {
    colorsStyling += `
    &[data-bgcolor="${i}"] {
        background-color: ${histogramBackgroundColors[i]};
    }`;
  }
  return colorsStyling;
};
const HistogramPercentage = styled(Overline)`
  flex-grow: 1;
  padding-top: ${spacing.quarter};
  padding-bottom: ${spacing.quarter};
  font-size: 10px;
  text-align: center;
  color: ${colors.white};
  ${createHistogramBackgroundColors()}
`;

const AlignEndContainer = styled.div`
  text-align: end;
`;

export const OptimizeSecondaryCaption = styled(Caption)`
  color: ${colors.textSecondary};
`;

type Props = {
  lastUpdated: string;
  histogramData: HistogramDataPoint[];
  refreshPeriod: string;
};

export const Histogram = ({ histogramData, refreshPeriod, lastUpdated }: Props) => {
  const sortedData = histogramData.concat().sort((a, b) => a.capNumber - b.capNumber);
  const relativeSizeEnhancedData = transformToVisualPercents(sortedData);

  return (
    <OptimizeResultsDataRow>
      <HistogramTitleRow>
        <AiLight16 />
        <Body2NoMargin>Audience distribution by optimized frequency</Body2NoMargin>
        <ClickTip
          popoverContentTitle="What does this mean?"
          popoverContentBody="Iterable AI has determined this breakdown by optimal sends based on different audience segments. The model is currently running in the background for the next 7 or 30 day cycle."
        />
      </HistogramTitleRow>
      <AlignEndContainer>
        <OptimizeSecondaryCaption>
          Refreshes {`${refreshPeriod}`} • Last updated on {lastUpdated}
        </OptimizeSecondaryCaption>
      </AlignEndContainer>
      {
        <HistogramContainer>
          {relativeSizeEnhancedData.map((item, index) => {
            const { capNumber, percentage, sizePercentage } = item;
            return (
              <HistogramItem key={capNumber} style={{ flexGrow: sizePercentage * 100 }}>
                <HistogramSends>{capNumber}</HistogramSends>
                <HistogramPercentage data-bgcolor={index}>{percentage}</HistogramPercentage>
              </HistogramItem>
            );
          })}
        </HistogramContainer>
      }
    </OptimizeResultsDataRow>
  );
};
